/**
 * Symmetry Commerce (https://symmetrycommerce.com)
 * ========== WHS INTEGRATION START ==========
 * 
 * WHS (Wholesale Price Display) Integration for Symmetry Quick View Modal
 * 
 * This module provides integration between the quick view modal and wholesale pricing apps
 * that use data-wpd-* attributes (like Wholesale Hero, Wholesale Pricing Discount apps).
 * 
 * Usage:
 *   const integration = new WHSIntegration(product, variant);
 *   const priceHTML = integration.wrapPrice(priceHTML);
 *   integration.trigger(); // Call after rendering
 * 
 * ========== WHS INTEGRATION END ==========
 */

class WHSIntegration {
  constructor(product, variant) {
    this.product = product;
    this.variant = variant;
  }

  /**
   * Generate WHS data attributes for a price element
   * Matches the format used in snippets/price.liquid
   */
  getDataAttributes() {
    if (!this.product || !this.variant) return '';
    
    const collectionIds = this.product.collections?.map(c => c.id).join(',') || '';
    
    const attrs = {
      'data-wpd-product-handle': this.product.handle,
      'data-wpd-variant-id': this.variant.id,
      'data-wpd-variant-price': this.variant.price,
      'data-wpd-variant-compare-at-price': this.variant.compare_at_price || '',
      'data-wpd-product-collection-ids': collectionIds,
      'data-wpd-product-id': this.product.id
    };
    
    return Object.entries(attrs)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
  }

  /**
   * Wrap a price string with WHS data attributes
   * @param {string} priceHTML - The price HTML to wrap
   * @returns {string} - Price HTML wrapped with WHS attributes
   */
  wrapPrice(priceHTML) {
    const attrs = this.getDataAttributes();
    return `<span ${attrs}>${priceHTML}</span>`;
  }

  /**
   * Wrap compare-at price with data-wpd-hide class
   * This allows WHS to hide the original compare price when showing wholesale pricing
   */
  wrapComparePrice(priceHTML) {
    return `<span class="data-wpd-hide">${priceHTML}</span>`;
  }

  /**
   * Trigger WHS app to process new price elements
   * Tries multiple methods since different WHS apps may use different APIs
   */
  trigger() {
    setTimeout(() => {
      // Custom event
      document.dispatchEvent(new CustomEvent('wpd:refresh'));
      
      // WPD object
      if (typeof window.WPD !== 'undefined' && typeof window.WPD.init === 'function') {
        window.WPD.init();
      }
      
      // Common wholesale app functions
      if (typeof window.initWholesalePricing === 'function') {
        window.initWholesalePricing();
      }
      
      if (typeof window.WholesalePriceDisplay !== 'undefined') {
        if (typeof window.WholesalePriceDisplay.refresh === 'function') {
          window.WholesalePriceDisplay.refresh();
        }
        if (typeof window.WholesalePriceDisplay.init === 'function') {
          window.WholesalePriceDisplay.init();
        }
      }

      // Trigger DOM mutation for MutationObserver
      const wpdElements = document.querySelectorAll('[data-wpd-variant-id]');
      wpdElements.forEach(el => {
        el.classList.add('wpd-needs-update');
        setTimeout(() => el.classList.remove('wpd-needs-update'), 10);
      });
    }, 100);
  }

  /**
   * Extract collection IDs from existing product cards on the page
   * Falls back to empty array if not found
   */
  static async fetchCollectionsFromDOM(productHandle) {
    const productCard = document.querySelector(`[data-wpd-product-handle="${productHandle}"]`);
    if (productCard) {
      const collectionIdsStr = productCard.getAttribute('data-wpd-product-collection-ids');
      if (collectionIdsStr) {
        return collectionIdsStr.split(',')
          .map(id => id.trim())
          .filter(id => id)
          .map(id => ({ id }));
      }
    }
    
    return [];
  }
}

// ========== WHS INTEGRATION START ==========
// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.WHSIntegration = WHSIntegration;
}
// ========== WHS INTEGRATION END ==========
